import type { FontScale } from './types';
import {
  SPREAD_DESIGN_W,
  SPREAD_TEXT_LINE_HEIGHT,
  formatPageInches,
  textSizeForFontScale,
} from './constants';

// ============================================================
// Page capacity — how many words actually fit on one page
// ============================================================

/**
 * Default text-block placement for a page's auto-flowed story text, in
 * percent of the INTERIOR SPREAD canvas (left page; the right page
 * mirrors it at x+50). Single source for the spread builder
 * (beat-pages.ts) and the capacity estimate below — if the band moves,
 * both stay in step.
 */
export const PAGE_TEXT_BAND = { x: 6, y: 56, w: 38, h: 34 } as const;

/**
 * Heuristics for the capacity estimate, all in spread design units (the
 * units font sizes are stored in — px at a SPREAD_DESIGN_W-wide spread):
 *   • side margins are the PAGE_TEXT_BAND's own insets (6% of the spread
 *     on each side of a 50%-wide page → 12% of the page),
 *   • vertically the estimate assumes text MAY use most of the page —
 *     capacity is the page's text budget, not the default band's height;
 *     the band only sets where text starts, and the author resizes it as
 *     the page fills,
 *   • an average word (plus trailing space) measures ~3.1em in the book
 *     faces at children's-book word lengths.
 * The result is an ESTIMATE for meters and grouping defaults, not layout —
 * the real text flow still happens in flowText with measured glyphs.
 */
const CAPACITY_MARGIN_X = PAGE_TEXT_BAND.x / 50; // band inset, page-relative
const CAPACITY_MARGIN_Y = 0.09;
const CAPACITY_EM_PER_WORD = 3.1;

/**
 * Approximate words a FULL-TEXT page holds at the project's font scale and
 * book format. One page is half an interior spread; the spread canvas is
 * SPREAD_DESIGN_W wide with height derived from the format's aspect.
 */
export function pageCapacityWords(
  fontScale: FontScale | null | undefined,
  formatPreset: string | null | undefined,
): number {
  const { w, h } = formatPageInches(formatPreset);
  const designH = SPREAD_DESIGN_W / ((2 * w) / h);
  const usableW = (SPREAD_DESIGN_W / 2) * (1 - 2 * CAPACITY_MARGIN_X);
  const usableH = designH * (1 - 2 * CAPACITY_MARGIN_Y);
  const size = textSizeForFontScale(fontScale);
  const lines = Math.floor(usableH / (size * SPREAD_TEXT_LINE_HEIGHT));
  const wordsPerLine = usableW / (size * CAPACITY_EM_PER_WORD);
  return lines * wordsPerLine;
}

/**
 * Emergent density readout for a page holding `words` of text: how full
 * the page is, and what that fullness means for the art. This replaces
 * "choose a density" — whatever the text doesn't use is art space.
 */
export type PageFillKind =
  | 'empty'
  | 'picture-led'
  | 'balanced'
  | 'text-led'
  | 'overflow';

export interface PageFillState {
  /** words ÷ capacity; 0 for an empty page. */
  fill: number;
  kind: PageFillKind;
  /** Fraction of the page left for art (0 when overflowing). */
  artShare: number;
}

export function pageFillState(words: number, capacity: number): PageFillState {
  if (words <= 0) return { fill: 0, kind: 'empty', artShare: 1 };
  const fill = capacity > 0 ? words / capacity : Infinity;
  const artShare = Math.max(0, 1 - fill);
  if (fill > 1) return { fill, kind: 'overflow', artShare };
  if (fill > 0.72) return { fill, kind: 'text-led', artShare };
  if (fill > 0.45) return { fill, kind: 'balanced', artShare };
  return { fill, kind: 'picture-led', artShare };
}

// ============================================================
// Beat breaks — page turns as gap indices between beats
// ============================================================

/**
 * A page turn AFTER beat i is the gap index i; valid gaps are
 * 0..beatCount-2. Breaks are stored on the project as a JSON number[].
 * Because pages are contiguous runs of beats, ANY set of valid gap
 * indices is a valid pagination — story order cannot break.
 */

/** Coerce an unknown value into a sorted, deduped, in-range breaks array.
 *  Returns null when the value is not an array of integers (out-of-range
 *  entries are dropped, not fatal — beat count may have changed). */
export function sanitizeBeatBreaks(
  value: unknown,
  beatCount: number,
): number[] | null {
  if (!Array.isArray(value)) return null;
  const out = new Set<number>();
  for (const v of value) {
    if (typeof v !== 'number' || !Number.isInteger(v)) return null;
    if (v >= 0 && v <= beatCount - 2) out.add(v);
  }
  return [...out].sort((a, b) => a - b);
}

/** Parse the project's stored beat_breaks JSON. Invalid/missing → []. */
export function parseBeatBreaks(
  raw: string | null | undefined,
  beatCount: number,
): number[] {
  if (!raw) return [];
  try {
    return sanitizeBeatBreaks(JSON.parse(raw), beatCount) ?? [];
  } catch {
    return [];
  }
}

/** Group beat indices into pages: contiguous runs delimited by breaks. */
export function pagesFromBreaks(
  beatCount: number,
  breaks: number[],
): number[][] {
  if (beatCount <= 0) return [];
  const breakSet = new Set(breaks);
  const pages: number[][] = [];
  let current: number[] = [];
  for (let i = 0; i < beatCount; i++) {
    current.push(i);
    if (breakSet.has(i) || i === beatCount - 1) {
      pages.push(current);
      current = [];
    }
  }
  return pages;
}

/**
 * Default pagination for freshly detected beats: greedily pack beats into
 * pages while the page stays at or under `targetFill` of capacity (a
 * "balanced" page with roughly 40% left for art). A beat larger than the
 * budget gets a page of its own — never split, only ever its own overflow.
 */
export function autoBeatBreaks(
  beatWordCounts: number[],
  capacity: number,
  targetFill = 0.6,
): number[] {
  const budget = Math.max(1, capacity * targetFill);
  const breaks: number[] = [];
  let pageWords = 0;
  for (let i = 0; i < beatWordCounts.length; i++) {
    const w = beatWordCounts[i];
    if (pageWords > 0 && pageWords + w > budget) {
      breaks.push(i - 1);
      pageWords = 0;
    }
    pageWords += w;
  }
  return breaks;
}
